import { useEffect, useState } from 'react';
import axios from 'axios';
import { HOME } from '@/constants/testIds';
import { track } from '@/lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const SizePill = ({ size, active, onClick, disabled }) => (
  <button
    type="button"
    data-testid={`${HOME.shopSizePill}-${size}`}
    onClick={onClick}
    disabled={disabled}
    className={[
      'inline-flex items-center justify-center font-display font-bold text-xs uppercase tracking-wide',
      'h-9 w-9 rounded-full border-2 transition-colors',
      active
        ? 'bg-brand-orange text-black border-brand-orange'
        : 'bg-transparent text-white/80 border-white/40 hover:border-white hover:text-white',
      disabled ? 'opacity-40 cursor-not-allowed' : '',
    ].join(' ')}
  >
    {size}
  </button>
);

const ProductCard = ({ product, onBuy, busyId }) => {
  const [size, setSize] = useState(product.sizes[2] || product.sizes[0]);
  const busy = busyId === product.id;
  const disabled = !product.available || busy;

  const extraSizes = product.extra_sizes || [];
  const extraCost = product.extra_size_cost || 0;
  const surcharge = extraSizes.includes(size) ? extraCost : 0;
  const finalPrice = product.price + surcharge;

  return (
    <div
      data-testid={HOME.shopCard}
      className="group relative flex flex-col bg-black text-white rounded-3xl overflow-hidden border-4 border-black shadow-[10px_10px_0_#FF6600] hover:shadow-[14px_14px_0_#FF6600] transition-shadow"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[radial-gradient(circle_at_50%_30%,#1a1a1a_0%,#000_70%)]">
        <img
          src={product.image}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          draggable="false"
        />
        {!product.available && (
          <div
            data-testid={HOME.shopComingSoon}
            className="absolute top-4 left-4 bg-brand-orange text-black font-display font-black text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full"
          >
            Coming Soon
          </div>
        )}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
          <span className="font-body text-xs tracking-[0.3em] uppercase text-white/70 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
            Drop · {product.color}
          </span>
          <span className="font-display font-black text-2xl bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
            ${product.price.toFixed(0)}
          </span>
        </div>
      </div>
      <div className="p-6 flex-1 flex flex-col gap-4">
        <h3 className="font-display font-black text-2xl leading-tight">{product.name}</h3>
        <p className="font-body text-sm text-white/70 leading-relaxed">{product.tagline}</p>

        <div className="mt-auto">
          <div className="flex items-center justify-between text-xs font-body tracking-[0.3em] uppercase text-white/40 mb-2">
            <span>Size</span>
            {extraSizes.length > 0 && (
              <span className="normal-case tracking-normal text-[10px] text-white/40">
                +${extraCost.toFixed(0)} for {extraSizes.join(' / ')}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {product.sizes.map((s) => (
              <SizePill key={s} size={s} active={size === s} onClick={() => setSize(s)} />
            ))}
          </div>
          <button
            type="button"
            data-testid={`${HOME.shopBuyBtn}-${product.id}`}
            onClick={() => onBuy(product, size)}
            disabled={disabled}
            className={[
              'w-full inline-flex items-center justify-center gap-2 font-display font-bold text-sm uppercase tracking-wide',
              'rounded-full px-6 py-4 transition-all duration-300',
              disabled
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-brand-orange text-black hover:bg-white hover:text-black shadow-[4px_4px_0_#FFF7EE] hover:shadow-[4px_4px_0_#FF6600]',
            ].join(' ')}
          >
            {busy
              ? 'Opening checkout...'
              : product.available
                ? <>Buy · ${(finalPrice * 1.0825).toFixed(2)}{surcharge > 0 && <span className="font-body font-normal normal-case text-[11px] tracking-normal opacity-80">(incl. {size} +${surcharge.toFixed(0)} + tax)</span>}{surcharge === 0 && <span className="font-body font-normal normal-case text-[11px] tracking-normal opacity-80">(incl. 8.25% tax)</span>}</>
                : 'Notify me when it drops'}
            {!busy && product.available && <span aria-hidden="true">→</span>}
          </button>
          {/* Payment method badges */}
          <div className="mt-3 flex items-center justify-center gap-3 text-[10px] font-body uppercase tracking-[0.25em] text-white/40">
            <span>Apple Pay</span>
            <span aria-hidden="true">·</span>
            <span>Cash App</span>
            <span aria-hidden="true">·</span>
            <span>Card</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShopSection = () => {
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        if (!cancelled) setProducts(data);
      } catch (e) {
        if (!cancelled) setError('Could not load the drop. Please refresh.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleBuy = async (product, size) => {
    setBusyId(product.id);
    setError(null);
    track('shop_buy_clicked', {
      product_id: product.id,
      product_name: product.name,
      size,
    });
    try {
      const { data } = await axios.post(`${API}/api/checkout/session`, {
        product_id: product.id,
        size,
        origin_url: window.location.origin,
      });
      track('checkout_started', { product_id: product.id, size });
      window.location.href = data.url;
    } catch (e) {
      setError(
        e.response?.data?.detail
        || 'Checkout could not start. Please try again.'
      );
      setBusyId(null);
    }
  };

  return (
    <section
      id="shop"
      data-testid={HOME.shopSection}
      className="relative bg-black text-white py-28 px-6 sm:px-12 overflow-hidden"
    >
      {/* Background flourish */}
      <div className="pointer-events-none absolute -top-32 -right-20 w-[36rem] h-[36rem] rounded-full bg-brand-orange/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[32rem] h-[32rem] rounded-full bg-brand-orange/5 blur-3xl" />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-3">
              The Shop
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em]">
              Tees that started<br />
              as <span className="text-brand-orange">bathroom thoughts.</span>
            </h2>
          </div>
          <p className="font-body text-sm text-white/60 max-w-sm">
            Small-batch drops, ships in <strong className="text-white">3 to 7 days</strong>. Apple Pay, Cash App, and major credit cards accepted at checkout. <span className="text-white/60">Prices include TX 8.25% sales tax.</span>
          </p>
        </div>

        {error && (
          <div className="mb-6 px-5 py-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 font-body text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} onBuy={handleBuy} busyId={busyId} />
          ))}
        </div>

        <p className="font-body text-xs text-white/40 text-center mt-12 tracking-[0.25em] uppercase">
          Hoodies available on custom orders — submit an idea below to lock one in.
        </p>
      </div>
    </section>
  );
};

export default ShopSection;
